import { buildCompanyInfoGroup } from '@/components/form/fieldDefs/companyInfoFields';
import { composeSchemaFormWithT } from '@/components/form/fieldDefs/composeForm';
import { buildPersonalInfoGroup } from '@/components/form/fieldDefs/personalInfoFields';
import { ACCEPTED_PRIVACY_POLICY_FIELD } from '@/components/form/fieldDefs/signUpPrivacyAcceptance';
import {
  assertPasswordsMatch,
  buildCommonValidationMessages,
  optionalString,
  passwordConfirmField,
  passwordField,
  requiredBoolean,
  requiredString,
} from '@/utils/validation/schemaHelpers';

import type { CompanyInfoFieldsOptions } from '@/components/form/fieldDefs/companyInfoFields';
import type { FormSchema } from '@/utils/validation/formSchemaKit';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';
import type { DefaultOptionType } from 'antd/es/select';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

type ClientInviteFormComposeOptions = {
  companyOptions?: DefaultOptionType[];
  onCompanySearch?: (value: string) => void;
  onCompanyChange?: (companyId: string) => void;
  companyId?: string;
  industryOptions?: DefaultOptionType[];
  regionOptions?: DefaultOptionType[];
  disableFields?: boolean;
  addressComponent?: ReactNode;
  fieldTestIds?: {
    email?: string;
    name?: string;
    company_name?: string;
  };
};

export const composeClientInviteForm = (
  t: TFunction,
  options: ClientInviteFormComposeOptions = {},
  scope: ValidationScope = 'save',
) =>
  composeSchemaFormWithT(
    t,
    [
      buildPersonalInfoGroup({
        t,
        title: t('PERSONAL_INFO_TITLE'),
        disableFields: options.disableFields,
        companyOptions: options.companyOptions,
        onCompanySearch: options.onCompanySearch,
        onCompanyChange: options.onCompanyChange,
        fieldTestIds: options.fieldTestIds
          ? {
              name: options.fieldTestIds.name,
              email: options.fieldTestIds.email,
            }
          : undefined,
      }),
      buildCompanyInfoGroup({
        t,
        title: t('COMPANY_INFO_TITLE'),
        disabled: options.disableFields,
        companyId: options.companyId,
        industryOptions: options.industryOptions,
        regionOptions: options.regionOptions,
        includeAddressField: Boolean(options.addressComponent),
        addressComponent: options.addressComponent,
        fieldTestIds: options.fieldTestIds?.company_name
          ? { company_name: options.fieldTestIds.company_name }
          : undefined,
      }),
    ],
    { scope },
  );

export const composeClientOnboardForm = (
  t: TFunction,
  options: Omit<CompanyInfoFieldsOptions, 't'> = {},
  scope: ValidationScope = 'save',
) =>
  composeSchemaFormWithT(
    t,
    [
      buildCompanyInfoGroup({
        t,
        title: t('COMPANY_INFO_TITLE'),
        ...options,
      }),
    ],
    {
      scope,
      extraFields: {
        image: optionalString(),
      },
    },
  );

export const buildClientInvitationFormSchema = (
  t: TFunction,
  scope: ValidationScope = 'save',
): FormSchema => {
  const messages = buildCommonValidationMessages(t);
  return composeSchemaFormWithT(
    t,
    [
      buildPersonalInfoGroup({
        t,
        disableFields: true,
        includeHiddenIds: false,
        phoneOptional: false,
      }),
    ],
    {
      scope,
      extraFields: {
        password: passwordField(messages),
        password_confirm: passwordConfirmField(messages),
        language: requiredString(messages.required),
        currency: requiredString(messages.required),
        [ACCEPTED_PRIVACY_POLICY_FIELD]: requiredBoolean(messages.required),
      },
      extraRefine: (data, ctx) => {
        assertPasswordsMatch(
          data,
          ctx,
          'password',
          'password_confirm',
          messages.passwordMismatch,
        );
      },
    },
  ).formSchema;
};

export const buildCompanyInfoFormSchema = (
  t: TFunction,
  scope: ValidationScope = 'save',
): FormSchema =>
  composeClientOnboardForm(
    t,
    {
      companyNameField: 'name',
    },
    scope,
  ).formSchema;

export type CompanyInfoFormValues = {
  name?: string;
  city?: string;
  office_locations?: { id: string; address: string }[] | null;
  industry?: string;
  region?: string;
  notable_clients?: string | null;
  number_of_employees?: number | string | null;
  average_age?: number | null;
  male_percentage?: number | null;
  dress_code?: string | null;
  description?: string | null;
  company_culture?: string | null;
  difference_description?: string | null;
  excluded_companies?: string | null;
  address?: string | null;
  image?: string;
};
